import type { Scene } from '@lumen5/luminary';
import { AspectRatio } from '@lumen5/luminary';
import type { TransitionManager } from '@lumen5/luminary/core/transition/TransitionManager';

import type { SceneDesign } from 'js/src/client';
import {
    type Direction,
    getTransitionUrlForDirection,
    NONE_TRANSITION_URL,
    RECOMMENDED_TRANSITION_URL,
    TRANSITION_URL_DATA,
    type TransitionUrl,
} from 'js/src/components/app/transitions-sidebar/transition-data';
import { Toast } from 'js/src/components/common/Toast';
import { getSceneDesign } from 'js/src/data/templates';
import type { RootState } from 'js/src/libs/RootState';
import { getSceneNumber } from 'js/src/luminary';
import { createLuminaryOperation } from 'js/src/luminary/luminary-hooks';

type SceneNumber = string | number | null;

export type TransitionSceneNumbers = {
    previousSceneNumber: SceneNumber;
    sceneNumber: SceneNumber;
};

// A transition lives on the second of the two scenes it joins: `scene` is that
// second scene and `scene.prevScene` is the first. Either can lack a number
// (e.g. voice-driven or outro scenes), in which case getSceneNumber returns null.
export const getTransitionSceneNumbers = (
    state: RootState,
    scene: Scene,
): TransitionSceneNumbers => ({
    previousSceneNumber: scene.prevScene ? getSceneNumber(state, scene.prevScene) : null,
    sceneNumber: getSceneNumber(state, scene),
});

type RawSceneDesign = { customTransition: TransitionManager };

const getSceneDesignForAspectRatio = (
    sceneDesign: SceneDesign,
    aspectRatio: AspectRatio,
): RawSceneDesign => {
    switch (aspectRatio) {
        case AspectRatio.LANDSCAPE:
            return sceneDesign.landscape_data as RawSceneDesign;
        case AspectRatio.VERTICAL:
            return sceneDesign.vertical_data as RawSceneDesign;
        case AspectRatio.SQUARE:
            return sceneDesign.square_data as RawSceneDesign;
        case AspectRatio.FOURFIVE:
            return sceneDesign.fourfive_data as RawSceneDesign;
        default:
            throw new Error('Invalid aspect ratio');
    }
};

// The transition baked into the scene design for the given aspect ratio, which is
// what the "Recommended" option applies. Null when the scene design has no transition.
export const getSceneDesignTransitionUrl = (
    sceneDesign: SceneDesign | null | undefined,
    aspectRatio: AspectRatio,
): TransitionUrl => {
    if (!sceneDesign) {
        return NONE_TRANSITION_URL;
    }
    return getSceneDesignForAspectRatio(sceneDesign, aspectRatio).customTransition.transitionUrl;
};

export const applyTransitionOperation = createLuminaryOperation(
    ({ luminary, getState }, sceneId: string, transitionUrl: TransitionUrl) => {
        const scene = luminary.getSceneById(sceneId);
        if (!scene) {
            throw new Error('Scene not found');
        }
        if (transitionUrl === RECOMMENDED_TRANSITION_URL) {
            const sceneDesign = getSceneDesign(getState(), scene.sceneDesign);
            transitionUrl = getSceneDesignTransitionUrl(sceneDesign, luminary.aspectRatio);
        }
        scene.customTransition.transitionUrl = transitionUrl;
    },
);

// An aspect-ratio-independent description of a scene's transition, used to carry the user's choice
// across an aspect ratio change (which rebuilds each scene from its scene design and would
// otherwise reset the transition). Luminary only stores the resolved, aspect-ratio-specific URL,
// so we capture the transition's group + direction here in order to look up the equivalent URL for
// the new aspect ratio afterwards.
export type PreservedTransition =
    | { kind: 'recommended' }
    | { kind: 'none' }
    | { kind: 'custom'; groupId: string; direction: Direction };

export const captureTransitionForAspectRatioChange = (
    state: RootState,
    scene: Scene,
    aspectRatio: AspectRatio,
): PreservedTransition => {
    const currentTransitionUrl = scene.customTransition.transitionUrl;
    const sceneDesignTransitionUrl = getSceneDesignTransitionUrl(
        getSceneDesign(state, scene.sceneDesign),
        aspectRatio,
    );
    // The transition matches the scene design's own transition, so let it keep following the scene
    // design — the rebuild already loads the new aspect ratio's recommended transition.
    if (currentTransitionUrl === sceneDesignTransitionUrl) {
        return { kind: 'recommended' };
    }
    if (currentTransitionUrl === NONE_TRANSITION_URL) {
        return { kind: 'none' };
    }
    const transitionData = currentTransitionUrl
        ? TRANSITION_URL_DATA[currentTransitionUrl]
        : undefined;
    // An unrecognized URL (e.g. a scene-design-specific transition) has no group/direction to
    // translate, so leave it to the scene design rather than guessing.
    if (!transitionData) {
        return { kind: 'recommended' };
    }
    return {
        kind: 'custom',
        groupId: transitionData.groupId,
        direction: transitionData.directionId,
    };
};

export const applyPreservedTransition = (
    scene: Scene,
    preservedTransition: PreservedTransition,
    aspectRatio: AspectRatio,
): void => {
    if (preservedTransition.kind === 'recommended') {
        return;
    }
    if (preservedTransition.kind === 'none') {
        scene.customTransition.transitionUrl = NONE_TRANSITION_URL;
        return;
    }
    scene.customTransition.transitionUrl = getTransitionUrlForDirection(
        preservedTransition.groupId,
        aspectRatio,
        preservedTransition.direction,
    );
};

const getTransitionAppliedMessage = ({
    previousSceneNumber,
    sceneNumber,
}: TransitionSceneNumbers): string | null => {
    if (previousSceneNumber !== null && sceneNumber !== null) {
        return `Transition applied between scenes ${previousSceneNumber} and ${sceneNumber}`;
    }
    if (sceneNumber !== null) {
        return `Transition applied into scene ${sceneNumber}`;
    }
    if (previousSceneNumber !== null) {
        return `Transition applied after scene ${previousSceneNumber}`;
    }
    return null;
};

export const showTransitionAppliedToastOperation = createLuminaryOperation(
    ({ luminary, getState }, sceneId: string) => {
        const scene = luminary.getSceneById(sceneId);
        if (!scene) {
            return;
        }
        const message = getTransitionAppliedMessage(getTransitionSceneNumbers(getState(), scene));
        if (message) {
            Toast.success(message);
        }
    },
);
